import CommonForm, { ItemElementPropsInterface } from '../../../../../components/common/common-form/common-form'
import { Button, DatePicker, Input, InputNumber, Select, TimePicker, Radio, Form, Card, Divider, Space, Tag, Popover } from 'antd'
import { ClockCircleOutlined, UserOutlined, CalculatorOutlined, CheckCircleOutlined, CloseCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { commonFormSelect, getSelectOptions } from '../../../../../components/common/common-form/common-form-select'
import { ApiRequest } from '../../../../../services/api/api'
import { urlKey, urlType } from '../../../../../services/api/api-urls'
import React, { useEffect, useState } from 'react'
import SweetAlertService from '../../../../../services/lib/utils/sweet-alert-service'
import moment from 'moment'
import { getCookie } from 'react-use-cookie'

interface IFormValues {
  machineCode?: string
  startupHours?: number
  startupMinutes?: number
  operators: Array<{
    id: number
    name: string
    employeeId?: number
    hours: number
    minutes: number
    status: string
  }>
  dailyCount?: number
  actualCount?: number
  lostCount?: number
}

interface WorkLogDialogProps {
  onDialogClose: (success: boolean) => void
  machineData?: any
}

const WorkLogDialog = (props: WorkLogDialogProps) => {
  const [form] = Form.useForm()
  const [initFormValues, setInitFormValues] = useState<IFormValues>()
  const [commonFormRef, setCommonFormRef] = useState<any>()
  const [employeeOptions, setEmployeeOptions] = useState<{ label: string; value: any }[]>([])
  const [operatorFields, setOperatorFields] = useState<number[]>([])
  // 当日已完成工单 (statusId === 10, madedAt 在当日): 用于 hover 显示工单号
  const [completedWorkOrders, setCompletedWorkOrders] = useState<any[]>([])

  useEffect(() => {
    const machineName = props.machineData?.machine?.name 

    const arrangementId = props.machineData?.arrangeId 
    if (arrangementId) {
      ApiRequest({
        url: `WorkingArrangement/GetWorkingArrangementById?id=${arrangementId}`,
        method: 'get'
      }).then((res: any) => {
        const data = res?.data?.data
        if (!data) return

        const uptime = Number(data.uptime || 0)
        const parsedHours = Math.floor(uptime / 60)
        const parsedMinutes = uptime % 60

        const logs = Array.isArray(data.workMachineLog || data.WorkMachineLog) ? (data.workMachineLog || data.WorkMachineLog) : []
        const parsedOperators = logs.length > 0
          ? logs.map((log: any, index: number) => ({
              id: log.logId || index + 1,
              name: '',
              employeeId: log.operator || undefined,
              hours: Math.floor(Number(log.duration || 0) / 60),
              minutes: Number(log.duration || 0) % 60,
              status: log.operationType === 'Operation' ? '生产' : '调机器'
            }))
          : [
              {
                id: 1,
                name: '',
                employeeId: data?.Operator || data?.operator || data?.OperatorNavigation?.employeeId || undefined,
                hours: parsedHours,
                minutes: parsedMinutes,
                status: '生产'
              }
            ]

        // 初始化操作员字段索引
        const initialFieldIndexes = parsedOperators.map((_: any, index: any) => index)
        setOperatorFields(initialFieldIndexes)

        setInitFormValues({
          machineCode: data?.machine?.machineName || machineName,
          startupHours: parsedHours, 
          startupMinutes: parsedMinutes,
          operators: parsedOperators,
          dailyCount: Number(data?.dailyCount ?? 0),
          actualCount: data.machineCount,
          lostCount: Math.max(0, (data?.dailyCount || 0) - (data.machineCount || 0)),
        })
      }).catch((error) => {
        console.error('Error fetching arrangement data:', error)
      })
    }

    ApiRequest({
      url: 'Employee/GetAllEmployee',
      method: 'get'
    }).then(res => {
      if (res) {
        const employees = res.data.data
        .filter((item: any) => !item.userName.toLowerCase().includes('test') && !item.userName.toLowerCase().includes('old'))
        .sort((a: any, b: any) => a.userName.localeCompare(b.userName))
        .map((item: any) => ({ 
            label: item.firstName.charAt(0).toUpperCase() + item.firstName.slice(1), 
            value: item.employeeId
        }))
        setEmployeeOptions(employees)
      }
    }).catch(error => {
      console.error('Error fetching employees:', error)
    })

    // 拉取该机器的当日生产记录, 后端已按 (machineId, date) 过滤 Completed
    // DTO 字段: WorkOrderId, WorkOrderNo, SerialNumber, ProductName, ProductionDate, ProductionQuantity
    const machineId = props.machineData?.machine?.id ?? props.machineData?.machine?.machineId
    const workingDate = props.machineData?.workingDate || props.machineData?.selectedDate
    const dateStr = workingDate ? new Date(workingDate).toISOString().slice(0, 10) : ''
    if (machineId && dateStr) {
      ApiRequest({
        url: `WorkOrder/GetProductionByMachineAndDate?machineId=${machineId}&date=${dateStr}`,
        method: 'get',
        isShowSpinner: false
      }).then((res: any) => {
        const records = Array.isArray(res?.data?.data) ? res.data.data : []
        setCompletedWorkOrders(records)
      }).catch((error: any) => {
        console.error('Error fetching production by machine and date:', error)
      })
    }
  }, [props.machineData])

  // 添加操作员字段
  const addOperatorField = () => {
    const newFieldIndex = operatorFields.length > 0 ? Math.max(...operatorFields) + 1 : 0
    setOperatorFields([...operatorFields, newFieldIndex])
    
    // 更新表单值
    const currentValues = commonFormRef?.getFieldsValue() || {}
    const currentOperators = currentValues.operators || []
    const updatedOperators = [
      ...currentOperators,
      {
        id: Date.now(),
        name: '',
        employeeId: undefined,
        hours: 0,
        minutes: 0,
        status: '生产'
      }
    ]
    
    commonFormRef?.setFieldsValue({
      operators: updatedOperators
    })
  }

  // 移除操作员字段
  const removeOperatorField = (index: number) => {
    const newFields = operatorFields.filter(fieldIndex => fieldIndex !== index)
    setOperatorFields(newFields)
    
    // 更新表单值
    const currentValues = commonFormRef?.getFieldsValue() || {}
    const currentOperators = currentValues.operators || []
    const updatedOperators = currentOperators.filter((_: any, i: number) => i !== index)
    
    commonFormRef?.setFieldsValue({
      operators: updatedOperators
    })
  }

  // 生成操作员表单项目
  const generateOperatorFormItems = (): ItemElementPropsInterface[] => {
    return operatorFields.map((fieldIndex, index) => ({
      name: `operator_${fieldIndex}`,
      label: ' ',
      isWholeRowWidth: true,
      inputElement: (
        <div 
          key={fieldIndex}
          style={{ 
            padding: '10px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #f0f0f0',
            marginBottom: '8px'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Form.Item
              name={['operators', index, 'employeeId']}
              style={{ margin: 0, flex: '1 1 150px', minWidth: '120px' }}
            >
              <Select 
                placeholder="操作员" 
                options={employeeOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                suffixIcon={<UserOutlined />}
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '6px', flex: '0 0 auto', alignItems: 'center' }}>
              <Form.Item name={['operators', index, 'hours']} style={{ margin: 0 }}>
                <InputNumber 
                  min={0} 
                  style={{ width: '65px' }} 
                  placeholder="小时"
                  controls={{ upIcon: '▲', downIcon: '▼' }}
                />
              </Form.Item>
              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>h</span>
              
              <Form.Item name={['operators', index, 'minutes']} style={{ margin: 0 }}>
                <InputNumber 
                  min={0} 
                  max={59} 
                  step={5}
                  style={{ width: '65px' }} 
                  placeholder="分钟"
                  controls={{ upIcon: '▲', downIcon: '▼' }}
                />
              </Form.Item>
              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>m</span>
            </div>
            
            <Form.Item name={['operators', index, 'status']} style={{ margin: 0, flex: '0 0 auto' }}>
              <Radio.Group>
                <Radio.Button value="生产">生产</Radio.Button>
                <Radio.Button value="调机器">调机器</Radio.Button>
              </Radio.Group>
            </Form.Item>
            
            {operatorFields.length > 1 && (
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => removeOperatorField(fieldIndex)}
              >删除
              </Button>
            )}
          </div>
        </div>
      ),
      hideLabel: true
    }))
  }

  // 生成完整的表单项目数组
  const getFormItems = (): ItemElementPropsInterface[] => {
    const baseItems: ItemElementPropsInterface[] = [
      // Machine Runtime Section
      {
        name: 'machineRuntimeSection',
        label: ' ',
        isWholeRowWidth: true,
        inputElement: (
          <Card 
            size="small"
            style={{ 
              marginBottom: '12px',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ClockCircleOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>总开机时长</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Form.Item name="startupHours" style={{ margin: 0, flex: 1 }}>
                  <InputNumber 
                    min={0} 
                    style={{ width: '100%' }} 
                    placeholder="小时"
                    controls={{ upIcon: '▲', downIcon: '▼' }}
                  />
                </Form.Item>
                <span style={{ fontSize: '13px', color: '#8c8c8c', minWidth: '40px' }}>Hours</span>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Form.Item name="startupMinutes" style={{ margin: 0, flex: 1 }}>
                  <InputNumber 
                    min={0} 
                    max={59} 
                    step={5}
                    style={{ width: '100%' }} 
                    placeholder="分钟"
                    controls={{ upIcon: '▲', downIcon: '▼' }}
                  />
                </Form.Item>
                <span style={{ fontSize: '13px', color: '#8c8c8c', minWidth: '40px' }}>Mins</span>
              </div>
            </div>
          </Card>
        ),
        // hideLabel: true
      },

      // Operator Records Section Header
      {
        name: 'operatorSectionHeader',
        label: ' ',
        isWholeRowWidth: true,
        inputElement: (
          <Card 
            size="small"
            style={{ 
              marginBottom: '12px',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <UserOutlined style={{ fontSize: '14px', color: '#52c41a' }} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>操作记录</span>
            </div>
            
            {/* 动态生成的操作员表单项目 */}
            {generateOperatorFormItems().map(item => item.inputElement)}
            
            <Button 
              type="dashed" 
              icon={<PlusOutlined />}
              style={{ 
                width: '100%', 
                marginTop: '8px',
                height: '36px',
                borderRadius: '4px'
              }}
              onClick={addOperatorField}
            >
              添加操作员
            </Button>
          </Card>
        ),
        // hideLabel: true
      },

      // Production Count Section
      {
        name: 'productionCountSection',
        label: ' ',
        isWholeRowWidth: true,
        inputElement: (
          <Card 
            size="small"
            style={{ 
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CalculatorOutlined style={{ fontSize: '14px', color: '#fa8c16' }} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>生产计数</span>
            </div>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <Form.Item 
                  name="dailyCount" 
                  label={<span style={{ fontSize: '13px', color: '#595959' }}>机器计数量</span>}
                  style={{ margin: 0, flex: 1 }}
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="输入机器计数量"
                    controls={{ upIcon: '▲', downIcon: '▼' }}
                  />
                </Form.Item>
                <Button 
                  type="primary" 
                  icon={<CheckCircleOutlined />}
                  style={{ 
                    height: '32px',
                    minWidth: '110px',
                    borderRadius: '4px'
                  }}
                  onClick={() => {
                    const activeForm = commonFormRef || form
                    const currentLostCount = activeForm.getFieldValue('lostCount')

                    if (currentLostCount < 0) {
                      SweetAlertService.errorMessage('损耗量不能小于 0，请检查输入数据！')
                    } else {
                      SweetAlertService.successMessage('计算完成！损耗量: ' + currentLostCount)
                    }
                  }}
                >
                  计算/检查
                </Button>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#fafafa',
                borderRadius: '4px'
              }}>
                                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '6px' }}>
                    工单完成数
                  </div>
                  {/* hover displays completed work order numbers */}
                  <Popover
                    content={
                      <div style={{ maxWidth: '320px' }}>
                        <div style={{ marginBottom: 6, fontWeight: 600 }}>
                          {`完成工单 (${completedWorkOrders.length})`}
                        </div>
                        {completedWorkOrders.length === 0 ? (
                          <div style={{ color: '#999', fontSize: '12px' }}>暂無</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                            {completedWorkOrders.map((wo: any) => (
                              <div
                                key={wo.WorkOrderId ?? wo.workOrderId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontSize: '12px',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: '#fafafa',
                                }}
                              >
                                <Tag color="green" style={{ margin: 0, minWidth: '60px', textAlign: 'center' }}>
                                  {wo.serialNumber }
                                </Tag>
                                <span style={{ color: '#1a3d2e', flex: 1, fontFamily: 'monospace' }}>
                                  {wo.productCode  ?? '-'}
                                </span>
                                <span style={{ color: '#52c41a', fontWeight: 600, minWidth: '50px', textAlign: 'right' }}>
                                  {wo.productionQuantity?? '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    }
                    title="完成工单号"
                    trigger="hover"
                    placement="top"
                  >
                    <Form.Item name="actualCount" style={{ margin: 0 }}>
                      <Input
                        readOnly
                        prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        style={{
                          backgroundColor: '#fff',
                          border: '1px solid #e8e8e8',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#262626',
                          cursor: 'pointer',
                          borderBottom: '1px dashed #52c41a'
                        }}
                      />
                    </Form.Item>
                  </Popover>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '6px' }}>
                    当日损耗量
                  </div>
                  <Form.Item shouldUpdate={(prev, cur) => prev.lostCount !== cur.lostCount} style={{ margin: 0 }}>
                    {({ getFieldValue }) => {
                      const lostCount = getFieldValue('lostCount')
                      const isNegative = lostCount < 0
                      return (
                        <Input
                          readOnly
                          prefix={
                            isNegative 
                              ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                              : <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          }
                          style={{
                            backgroundColor: isNegative ? '#fff2f0' : '#fff',
                            border: `1px solid ${isNegative ? '#ffccc7' : '#e8e8e8'}`,
                            fontSize: '14px',
                            fontWeight: 500,
                            color: isNegative ? '#ff4d4f' : '#262626'
                          }}
                          value={lostCount}
                        />
                      )
                    }}
                  </Form.Item>
                </div>
              </div>
            </Space>
          </Card>
        ),
        // hideLabel: true
      }
    ];

    return baseItems;
  }

  const onFormChange = (changedValues: any, newValues: any, form: any) => {
    if (changedValues.hasOwnProperty('dailyCount') || changedValues.hasOwnProperty('actualCount')) {
      const dailyCount = Number(newValues.dailyCount || 0)
      const actualCount = Number(newValues.actualCount || 0)
      const lostCount = dailyCount - actualCount
      
      form.setFieldsValue({ lostCount })
    }
  }

  const onFormBlur = (formInstance: any) => {
    if (!commonFormRef) {
      setCommonFormRef(formInstance)
    }
  }

  const onConfirm = async () => {
    try {
      const activeForm = commonFormRef || form
      const values = await activeForm.validateFields()
      
      const lostCount = values.dailyCount - values.actualCount 

      if (lostCount < 0) {
        await SweetAlertService.errorMessage('损耗量不能小于 0，请检查输入数据！')
        return 
      }

      const uptime = (Number(values.startupHours || 0) * 60) + Number(values.startupMinutes || 0)
      const ops: any[] = Array.isArray(values.operators) ? values.operators : []

      const workMachineLogs = ops.map((op: any) => ({
        arrangementId: props.machineData?.arrangeId,
        operator: op.employeeId,
        duration: (Number(op.hours || 0) * 60) + Number(op.minutes || 0),
        operationType: op.status === '调机器' ? 'Machine_Setup' : 'Operation'
      }))

      const payload = {
        arrangementId: props.machineData?.arrangeId || 0,
        uptime: uptime,
        dailyCount: Number(values.dailyCount || 0),
        workMachineLogs
      }

      console.log('UpdateWorkingArrangementWithLogs payload:', payload)

      const result = await ApiRequest({
        url: 'WorkingArrangement/UpdateWorkingArrangementWithLogs',
        method: 'put',
        data: payload,
        isShowSpinner: true
      })

      if (result) {
        await SweetAlertService.successMessage('提交成功')
        props.onDialogClose(true)
      }
    } catch (error) {
      console.error('表单验证失败:', error)
    }
  }

  const onCancel = () => {
    props.onDialogClose(false)
  }

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '900px', 
      margin: '0 auto', 
      padding: '0'
    }}>
    

      {/* Form Content */}
      <div style={{
        padding: '6px 12px',
        backgroundColor: '#fff'
      }}>
        <CommonForm 
          items={getFormItems()} 
          onFormChange={onFormChange} 
          onFormBlur={onFormBlur} 
          initFormValues={initFormValues}
        />
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '6px 24px',
        backgroundColor: '#fafafa',
        borderTop: '1px solid #f0f0f0',
        borderRadius: '0 0 8px 8px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px'
      }}>
        <Button
          onClick={onCancel}
          style={{
            minWidth: '90px',
            height: '36px',
            borderRadius: '4px'
          }}
        >
          取消
        </Button>
        <Button
          onClick={onConfirm}
          type="primary"
          icon={<CheckCircleOutlined />}
          style={{
            minWidth: '90px',
            height: '36px',
            backgroundColor: '#52c41a',
            borderColor: '#52c41a',
            borderRadius: '4px'
          }}
        >
          确认提交
        </Button>
      </div>
    </div>
  )
}

export default WorkLogDialog